/**
 * ContextPad — bpmn-js style floating action pad around selected elements.
 *
 * Shows small SVG icon buttons organized in sub-menus (Tasks, Gateways, Events)
 * positioned at the top-right corner of the selected node.
 *
 * Phase 8: Full BPMN element coverage, sub-menus, Event-Based Gateway constraints.
 */

import { v4 as uuid } from 'uuid';

const NEXT_X = 180;

// ── SVG icon paths ───────────────────────────────────────────────────────────

const ICONS = {
  // Tasks
  userTask: `<svg width="20" height="20" viewBox="0 0 20 20">
    <rect x="1" y="1" width="18" height="14" rx="2" fill="#e6f7ff" stroke="#1890ff" stroke-width="1.5"/>
    <circle cx="7" cy="6" r="2" fill="none" stroke="#1890ff" stroke-width="1"/>
    <path d="M 4 13 Q 7 10 10 13" fill="none" stroke="#1890ff" stroke-width="1"/>
  </svg>`,

  serviceTask: `<svg width="20" height="20" viewBox="0 0 20 20">
    <rect x="1" y="1" width="18" height="14" rx="2" fill="#f9f0ff" stroke="#722ed1" stroke-width="1.5"/>
    <circle cx="8" cy="8" r="3" fill="none" stroke="#722ed1" stroke-width="1"/>
    <circle cx="8" cy="8" r="1" fill="#722ed1"/>
  </svg>`,

  sendTask: `<svg width="20" height="20" viewBox="0 0 20 20">
    <rect x="1" y="1" width="18" height="14" rx="2" fill="#f0f5ff" stroke="#1d39c4" stroke-width="1.5"/>
    <path d="M 5 4 L 10 8 L 15 4 L 15 11 L 5 11 Z" fill="#1d39c4" stroke="#1d39c4" stroke-width="0.8"/>
    <path d="M 5 4 L 10 8 L 15 4" fill="none" stroke="#fff" stroke-width="0.8"/>
  </svg>`,

  receiveTask: `<svg width="20" height="20" viewBox="0 0 20 20">
    <rect x="1" y="1" width="18" height="14" rx="2" fill="#e6f7ff" stroke="#40a9ff" stroke-width="1.5"/>
    <rect x="5" y="4" width="10" height="7" fill="none" stroke="#40a9ff" stroke-width="0.9"/>
    <path d="M 5 4 L 10 8 L 15 4" fill="none" stroke="#40a9ff" stroke-width="0.9"/>
  </svg>`,

  scriptTask: `<svg width="20" height="20" viewBox="0 0 20 20">
    <rect x="1" y="1" width="18" height="14" rx="2" fill="#fff7e6" stroke="#fa8c16" stroke-width="1.5"/>
    <line x1="5" y1="5" x2="13" y2="5" stroke="#fa8c16" stroke-width="0.9"/>
    <line x1="5" y1="8" x2="13" y2="8" stroke="#fa8c16" stroke-width="0.9"/>
    <line x1="5" y1="11" x2="10" y2="11" stroke="#fa8c16" stroke-width="0.9"/>
  </svg>`,

  manualTask: `<svg width="20" height="20" viewBox="0 0 20 20">
    <rect x="1" y="1" width="18" height="14" rx="2" fill="#e6fffb" stroke="#13c2c2" stroke-width="1.5"/>
    <path d="M 6 12 L 6 7 C 6 5 8 4 9 6 L 10 5 C 11 4 12 5 12 6 L 13 6 L 13 12 Z" fill="none" stroke="#13c2c2" stroke-width="0.9"/>
  </svg>`,

  businessRuleTask: `<svg width="20" height="20" viewBox="0 0 20 20">
    <rect x="1" y="1" width="18" height="14" rx="2" fill="#fffbe6" stroke="#d48806" stroke-width="1.5"/>
    <rect x="5" y="3" width="10" height="9" fill="none" stroke="#d48806" stroke-width="0.8"/>
    <rect x="5" y="3" width="10" height="3" fill="#d48806" stroke="#d48806" stroke-width="0.8"/>
    <line x1="5" y1="9" x2="15" y2="9" stroke="#d48806" stroke-width="0.6"/>
    <line x1="9" y1="6" x2="9" y2="12" stroke="#d48806" stroke-width="0.6"/>
  </svg>`,

  callActivity: `<svg width="20" height="20" viewBox="0 0 20 20">
    <rect x="1" y="1" width="18" height="14" rx="2" fill="#f6ffed" stroke="#389e0d" stroke-width="2.5"/>
    <path d="M 6 5 L 11 5 L 11 3 L 14 7.5 L 11 12 L 11 10 L 6 10 Z" fill="none" stroke="#389e0d" stroke-width="0.9"/>
  </svg>`,

  subProcess: `<svg width="20" height="20" viewBox="0 0 20 20">
    <rect x="1" y="1" width="18" height="14" rx="2" fill="#e6f7ff" stroke="#096dd9" stroke-width="2"/>
    <rect x="6" y="8" width="8" height="5" rx="1" fill="none" stroke="#096dd9" stroke-width="0.8"/>
    <path d="M 10 9 L 10 12 M 8.5 10.5 L 11.5 10.5" stroke="#096dd9" stroke-width="0.7"/>
  </svg>`,

  transaction: `<svg width="20" height="20" viewBox="0 0 20 20">
    <rect x="1" y="1" width="18" height="14" rx="2" fill="#e6f7ff" stroke="#096dd9" stroke-width="2"/>
    <rect x="3" y="3" width="14" height="10" rx="1" fill="none" stroke="#096dd9" stroke-width="1"/>
    <rect x="6" y="8" width="8" height="5" rx="1" fill="none" stroke="#096dd9" stroke-width="0.8"/>
    <path d="M 10 9 L 10 12 M 8.5 10.5 L 11.5 10.5" stroke="#096dd9" stroke-width="0.7"/>
  </svg>`,

  // Gateways
  exclusiveGateway: `<svg width="20" height="20" viewBox="0 0 20 20">
    <polygon points="10,2 18,10 10,18 2,10" fill="#fffbe6" stroke="#faad14" stroke-width="1.5"/>
    <path d="M 7 7 L 13 13 M 13 7 L 7 13" stroke="#faad14" stroke-width="1.5" fill="none"/>
  </svg>`,

  parallelGateway: `<svg width="20" height="20" viewBox="0 0 20 20">
    <polygon points="10,2 18,10 10,18 2,10" fill="#e6fffb" stroke="#13c2c2" stroke-width="1.5"/>
    <path d="M 10 5 L 10 15 M 5 10 L 15 10" stroke="#13c2c2" stroke-width="2" fill="none"/>
  </svg>`,

  inclusiveGateway: `<svg width="20" height="20" viewBox="0 0 20 20">
    <polygon points="10,2 18,10 10,18 2,10" fill="#fff0f6" stroke="#eb2f96" stroke-width="1.5"/>
    <circle cx="10" cy="10" r="4" fill="none" stroke="#eb2f96" stroke-width="1.5"/>
  </svg>`,

  complexGateway: `<svg width="20" height="20" viewBox="0 0 20 20">
    <polygon points="10,2 18,10 10,18 2,10" fill="#fafafa" stroke="#595959" stroke-width="1.5"/>
    <path d="M 10 5 L 10 15 M 5 10 L 15 10 M 7 7 L 13 13 M 13 7 L 7 13" stroke="#595959" stroke-width="1.2" fill="none"/>
  </svg>`,

  eventBasedGateway: `<svg width="20" height="20" viewBox="0 0 20 20">
    <polygon points="10,2 18,10 10,18 2,10" fill="#f6ffed" stroke="#389e0d" stroke-width="1.5"/>
    <circle cx="10" cy="10" r="5" fill="none" stroke="#389e0d" stroke-width="1"/>
    <circle cx="10" cy="10" r="3.5" fill="none" stroke="#389e0d" stroke-width="1"/>
  </svg>`,

  // Events
  endEvent: `<svg width="20" height="20" viewBox="0 0 20 20">
    <circle cx="10" cy="10" r="8" fill="#fff1f0" stroke="#f5222d" stroke-width="3"/>
  </svg>`,

  intermediateEvent: `<svg width="20" height="20" viewBox="0 0 20 20">
    <circle cx="10" cy="10" r="8" fill="#fff" stroke="#1890ff" stroke-width="1.5"/>
    <circle cx="10" cy="10" r="5" fill="none" stroke="#1890ff" stroke-width="1.2"/>
  </svg>`,

  intermediateThrowEvent: `<svg width="20" height="20" viewBox="0 0 20 20">
    <circle cx="10" cy="10" r="8" fill="#fff" stroke="#1890ff" stroke-width="1.5"/>
    <circle cx="10" cy="10" r="5" fill="none" stroke="#1890ff" stroke-width="1.2"/>
    <polygon points="10,5 13.5,11.5 6.5,11.5" fill="#1890ff" stroke="#1890ff" stroke-width="0.5"/>
  </svg>`,

  boundaryEvent: `<svg width="20" height="20" viewBox="0 0 20 20">
    <circle cx="10" cy="10" r="8" fill="#fff2e8" stroke="#fa541c" stroke-width="1.5"/>
    <circle cx="10" cy="10" r="5" fill="none" stroke="#fa541c" stroke-width="1"/>
  </svg>`,

  // Actions
  textAnnotation: `<svg width="20" height="20" viewBox="0 0 20 20">
    <path d="M 7 2 L 2 2 L 2 16 L 7 16" fill="none" stroke="#555" stroke-width="1.5"/>
    <line x1="5" y1="6" x2="16" y2="6" stroke="#555" stroke-width="0.8"/>
    <line x1="5" y1="9.5" x2="14" y2="9.5" stroke="#555" stroke-width="0.8"/>
    <line x1="5" y1="13" x2="11" y2="13" stroke="#555" stroke-width="0.8"/>
  </svg>`,

  connect: `<svg width="16" height="16" viewBox="0 0 16 16">
    <path d="M 2 14 L 14 2" stroke="#555" stroke-width="1.5"/>
    <polygon points="14,2 10,3 13,6" fill="#555"/>
  </svg>`,

  edit: `<svg width="16" height="16" viewBox="0 0 16 16">
    <path d="M 11.5 1.5 L 14.5 4.5 L 5.5 13.5 L 1.5 14.5 L 2.5 10.5 Z"
          fill="none" stroke="#555" stroke-width="1.2" stroke-linejoin="round"/>
    <path d="M 10 3 L 13 6" fill="none" stroke="#555" stroke-width="1"/>
  </svg>`,

  delete: `<svg width="16" height="16" viewBox="0 0 16 16">
    <path d="M 4 4 L 12 12 M 12 4 L 4 12" stroke="#f5222d" stroke-width="2" stroke-linecap="round"/>
  </svg>`,

  // Sub-menu expand icons
  moreTasks: `<svg width="20" height="20" viewBox="0 0 20 20">
    <rect x="1" y="1" width="18" height="14" rx="2" fill="#f5f5f5" stroke="#999" stroke-width="1"/>
    <circle cx="6" cy="8" r="1.5" fill="#999"/>
    <circle cx="10" cy="8" r="1.5" fill="#999"/>
    <circle cx="14" cy="8" r="1.5" fill="#999"/>
  </svg>`,

  moreGateways: `<svg width="20" height="20" viewBox="0 0 20 20">
    <polygon points="10,2 18,10 10,18 2,10" fill="#f5f5f5" stroke="#999" stroke-width="1.5"/>
    <circle cx="7" cy="10" r="1.2" fill="#999"/>
    <circle cx="10" cy="10" r="1.2" fill="#999"/>
    <circle cx="13" cy="10" r="1.2" fill="#999"/>
  </svg>`,

  moreEvents: `<svg width="20" height="20" viewBox="0 0 20 20">
    <circle cx="10" cy="10" r="8" fill="#f5f5f5" stroke="#999" stroke-width="1.5"/>
    <circle cx="7" cy="10" r="1.2" fill="#999"/>
    <circle cx="10" cy="10" r="1.2" fill="#999"/>
    <circle cx="13" cy="10" r="1.2" fill="#999"/>
  </svg>`,

  replace: `<svg width="16" height="16" viewBox="0 0 16 16">
    <rect x="1" y="1" width="6" height="5" rx="1" fill="none" stroke="#555" stroke-width="1.1"/>
    <rect x="9" y="1" width="6" height="5" rx="1" fill="#e6f0ff" stroke="#1d4ed8" stroke-width="1.1"/>
    <rect x="1" y="9" width="6" height="5" rx="1" fill="none" stroke="#555" stroke-width="1.1"/>
    <rect x="9" y="9" width="6" height="5" rx="1" fill="none" stroke="#555" stroke-width="1.1"/>
  </svg>`,

  addNode: `<svg width="16" height="16" viewBox="0 0 16 16">
    <rect x="1" y="2" width="14" height="10" rx="2" fill="none" stroke="#555" stroke-width="1.2"/>
    <path d="M8 5v4M6 7h4" stroke="#555" stroke-width="1.5" stroke-linecap="round"/>
  </svg>`,

  // Lane operations
  addLane: `<svg width="20" height="20" viewBox="0 0 20 20">
    <rect x="1" y="1" width="18" height="8" fill="none" stroke="#333" stroke-width="1.2"/>
    <rect x="1" y="9" width="18" height="8" fill="none" stroke="#333" stroke-width="1.2"/>
    <line x1="4" y1="1" x2="4" y2="17" stroke="#333" stroke-width="1"/>
    <circle cx="14" cy="13" r="4" fill="#52c41a" stroke="none"/>
    <path d="M 14 11 L 14 15 M 12 13 L 16 13" stroke="#fff" stroke-width="1.5"/>
  </svg>`,

  addLaneAbove: `<svg width="20" height="20" viewBox="0 0 20 20">
    <rect x="1" y="8" width="18" height="9" fill="#e6f7ff" stroke="#333" stroke-width="1.2"/>
    <rect x="1" y="1" width="18" height="7" fill="none" stroke="#333" stroke-width="1.2" stroke-dasharray="3,2"/>
    <path d="M 10 2 L 10 7 M 7.5 4.5 L 12.5 4.5" stroke="#1890ff" stroke-width="1.5"/>
  </svg>`,

  addLaneBelow: `<svg width="20" height="20" viewBox="0 0 20 20">
    <rect x="1" y="1" width="18" height="9" fill="#e6f7ff" stroke="#333" stroke-width="1.2"/>
    <rect x="1" y="10" width="18" height="7" fill="none" stroke="#333" stroke-width="1.2" stroke-dasharray="3,2"/>
    <path d="M 10 11 L 10 16 M 7.5 13.5 L 12.5 13.5" stroke="#1890ff" stroke-width="1.5"/>
  </svg>`,

  removeLane: `<svg width="16" height="16" viewBox="0 0 16 16">
    <rect x="1" y="1" width="14" height="6" fill="none" stroke="#333" stroke-width="1"/>
    <rect x="1" y="7" width="14" height="6" fill="#fff1f0" stroke="#f5222d" stroke-width="1"/>
    <path d="M 5 10 L 11 10" stroke="#f5222d" stroke-width="1.5"/>
  </svg>`,
};

// ── Append panel tab definitions (uses existing colored ICONS) ───────────────

const APPEND_TABS = {
  tasks: {
    label: 'Tareas',
    groups: [
      {
        label: 'Básicas',
        items: [
          { type: 'epx:UserTask',         icon: 'userTask',         label: 'User Task',          shortLabel: 'User Task' },
          { type: 'epx:ServiceTask',       icon: 'serviceTask',      label: 'Service Task',        shortLabel: 'Service' },
          { type: 'epx:ScriptTask',        icon: 'scriptTask',       label: 'Script Task',         shortLabel: 'Script' },
          { type: 'epx:ManualTask',        icon: 'manualTask',       label: 'Manual Task',         shortLabel: 'Manual' },
          { type: 'epx:SendTask',          icon: 'sendTask',         label: 'Send Task',           shortLabel: 'Send' },
          { type: 'epx:ReceiveTask',       icon: 'receiveTask',      label: 'Receive Task',        shortLabel: 'Receive' },
          { type: 'epx:BusinessRuleTask',  icon: 'businessRuleTask', label: 'Business Rule Task',  shortLabel: 'Rule' },
        ],
      },
      {
        label: 'Subprocesos',
        items: [
          { type: 'epx:CallActivity', icon: 'callActivity', label: 'Call Activity', shortLabel: 'Call Activity' },
          { type: 'epx:SubProcess',   icon: 'subProcess',   label: 'Sub-Process',   shortLabel: 'Sub Process' },
          { type: 'epx:Transaction',  icon: 'transaction',  label: 'Transaction',   shortLabel: 'Transaction' },
        ],
      },
    ],
  },
  gateways: {
    label: 'Gateways',
    groups: [
      {
        label: 'Tipos',
        items: [
          { type: 'epx:ExclusiveGateway',  icon: 'exclusiveGateway',  label: 'Exclusive Gateway',   shortLabel: 'Exclusivo' },
          { type: 'epx:ParallelGateway',   icon: 'parallelGateway',   label: 'Parallel Gateway',    shortLabel: 'Paralelo' },
          { type: 'epx:InclusiveGateway',  icon: 'inclusiveGateway',  label: 'Inclusive Gateway',   shortLabel: 'Inclusivo' },
          { type: 'epx:ComplexGateway',    icon: 'complexGateway',    label: 'Complex Gateway',     shortLabel: 'Complejo' },
          { type: 'epx:EventBasedGateway', icon: 'eventBasedGateway', label: 'Event-Based Gateway', shortLabel: 'Por Evento' },
        ],
      },
    ],
  },
  events: {
    label: 'Eventos',
    groups: [
      {
        label: 'Tipos',
        items: [
          { type: 'epx:IntermediateEvent',      icon: 'intermediateEvent',      label: 'Intermediate Catch', shortLabel: 'Catch' },
          { type: 'epx:IntermediateThrowEvent',  icon: 'intermediateThrowEvent', label: 'Intermediate Throw', shortLabel: 'Throw' },
          { type: 'epx:EndEvent',               icon: 'endEvent',               label: 'End Event',          shortLabel: 'End' },
        ],
      },
    ],
  },
};

// Event-Based Gateway: restricted set
const APPEND_TABS_EVENTGW = {
  events: {
    label: 'Permitidos',
    groups: [
      {
        label: 'Después de Gateway por Evento',
        items: [
          { type: 'epx:IntermediateEvent', icon: 'intermediateEvent', label: 'Intermediate Catch', shortLabel: 'Catch' },
          { type: 'epx:ReceiveTask',       icon: 'receiveTask',       label: 'Receive Task',       shortLabel: 'Receive' },
          { type: 'epx:EndEvent',          icon: 'endEvent',          label: 'End Event',          shortLabel: 'End' },
        ],
      },
    ],
  },
};

// ── CSS (injected once) ──────────────────────────────────────────────────────

let cssInjected = false;

function injectCSS() {
  if (cssInjected) return;
  cssInjected = true;
  const style = document.createElement('style');
  style.textContent = `
    /* ── Compact context pad ── */
    .epx-context-pad {
      position: absolute;
      display: flex;
      flex-direction: column;
      gap: 2px;
      padding: 4px;
      background: #ffffff;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      box-shadow: 0 4px 16px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.06);
      z-index: 200;
      pointer-events: auto;
    }
    .epx-context-pad .epx-cp-divider {
      width: 100%;
      height: 1px;
      background: #f1f5f9;
      margin: 2px 0;
    }
    .epx-context-pad .epx-cp-btn {
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid transparent;
      border-radius: 6px;
      background: transparent;
      cursor: pointer;
      padding: 0;
      transition: background 0.1s, border-color 0.1s;
      position: relative;
      color: #475569;
    }
    .epx-context-pad .epx-cp-btn:hover {
      background: #eff6ff;
      border-color: #bfdbfe;
      color: #1d4ed8;
    }
    .epx-context-pad .epx-cp-btn.epx-cp-active {
      background: #eff6ff;
      border-color: #3b82f6;
    }
    .epx-context-pad .epx-cp-btn.epx-cp-danger:hover {
      background: #fef2f2;
      border-color: #fecaca;
      color: #dc2626;
    }
    .epx-context-pad .epx-cp-btn[data-tooltip]:hover::after {
      content: attr(data-tooltip);
      position: absolute;
      left: calc(100% + 8px);
      top: 50%;
      transform: translateY(-50%);
      background: #1e293b;
      color: #f1f5f9;
      padding: 3px 8px;
      border-radius: 4px;
      font-size: 10px;
      white-space: nowrap;
      z-index: 300;
      pointer-events: none;
      font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
    }

    /* ── Append panel popover ── */
    .epx-append-panel {
      position: absolute;
      width: 292px;
      background: #ffffff;
      border: 1px solid #e2e8f0;
      border-radius: 10px;
      box-shadow: 0 8px 32px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.07);
      z-index: 250;
      overflow: hidden;
      font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
      font-size: 12px;
    }
    .epx-ap-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 10px 14px;
      border-bottom: 1px solid #f1f5f9;
    }
    .epx-ap-title {
      font-size: 10px;
      font-weight: 700;
      color: #0f172a;
      letter-spacing: 0.07em;
      text-transform: uppercase;
    }
    .epx-ap-close {
      width: 22px;
      height: 22px;
      border-radius: 5px;
      border: none;
      background: transparent;
      color: #94a3b8;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      padding: 0;
    }
    .epx-ap-close:hover { background: #f1f5f9; color: #475569; }
    .epx-ap-tabs {
      display: flex;
      padding: 0 14px;
      border-bottom: 1px solid #f1f5f9;
    }
    .epx-ap-tab {
      padding: 7px 10px;
      font-size: 11px;
      color: #64748b;
      cursor: pointer;
      border-bottom: 2px solid transparent;
      margin-bottom: -1px;
      user-select: none;
    }
    .epx-ap-tab:hover { color: #1d4ed8; }
    .epx-ap-tab.epx-ap-tab-active {
      color: #1d4ed8;
      border-bottom-color: #1d4ed8;
      font-weight: 600;
    }
    .epx-ap-body {
      max-height: 280px;
      overflow-y: auto;
      padding-bottom: 6px;
    }
    .epx-ap-body::-webkit-scrollbar { width: 4px; }
    .epx-ap-body::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 2px; }
    .epx-ap-group-label {
      font-size: 9px;
      font-weight: 700;
      color: #94a3b8;
      letter-spacing: 0.09em;
      text-transform: uppercase;
      padding: 9px 13px 4px;
    }
    .epx-ap-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 3px;
      padding: 0 9px 4px;
    }
    .epx-ap-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 3px;
      padding: 6px 3px 5px;
      border-radius: 7px;
      cursor: pointer;
      border: 1.5px solid transparent;
      transition: background 0.1s, border-color 0.1s;
      user-select: none;
    }
    .epx-ap-item:hover {
      background: #f8fafc;
      border-color: #e2e8f0;
    }
    .epx-ap-icon {
      width: 34px;
      height: 26px;
      border-radius: 5px;
      border: 1.5px solid #e2e8f0;
      background: #f8fafc;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: border-color 0.1s, background 0.1s;
    }
    .epx-ap-item:hover .epx-ap-icon {
      border-color: #bfdbfe;
      background: #f0f9ff;
    }
    .epx-ap-icon svg { width: 16px; height: 16px; }
    .epx-ap-item-label {
      font-size: 9px;
      color: #64748b;
      text-align: center;
      line-height: 1.25;
    }
  `;
  document.head.appendChild(style);
}

// (type groups moved into APPEND_TABS / APPEND_TABS_EVENTGW above)

// ── ContextPad class ─────────────────────────────────────────────────────────

export class ContextPad {
  constructor(lf, i18n) {
    this._lf = lf;
    this._i18n = i18n || {};
    this._activeNode = null;
    this._padEl = null;
    this._replaceMenu = null;
    this._containment = null;
    this._appendPanelEl = null;
    this._appendPanelClickHandler = null;

    injectCSS();
    this._createPadElement();
    this._bind();
  }

  /** Allow EpxFlow to inject the ContainmentBehavior reference for lane operations */
  setContainment(containment) {
    this._containment = containment;
  }

  setReplaceMenu(replaceMenu) {
    this._replaceMenu = replaceMenu;
  }

  _createPadElement() {
    this._padEl = document.createElement('div');
    this._padEl.className = 'epx-context-pad';
    this._padEl.style.display = 'none';
  }

  _bind() {
    this._lf.on('node:click', ({ data }) => {
      this._show(data);
    });

    this._lf.on('blank:click', () => {
      this._hide();
    });

    this._lf.on('node:delete', () => {
      this._hide();
    });

    this._lf.on('node:drag', () => {
      this._hide();
    });

    this._lf.on('graph:transform', () => {
      if (this._activeNode) {
        this._reposition();
        if (this._appendPanelEl) {
          this._positionAppendPanel(this._activeNode);
        }
      }
    });
  }

  _show(nodeData) {
    this._activeNode = nodeData;
    this._hideAppendPanel();
    this._buildButtons(nodeData);
    this._reposition();
    this._padEl.style.display = 'flex';

    const container = this._lf.graphModel.rootEl;
    if (container && !container.contains(this._padEl)) {
      container.appendChild(this._padEl);
    }
  }

  _hide() {
    this._activeNode = null;
    this._hideAppendPanel();
    this._padEl.style.display = 'none';
  }

  _reposition() {
    if (!this._activeNode) return;

    const data = this._activeNode;
    const model = this._lf.graphModel.getElement(data.id);
    if (!model) return;

    const w = model.width || 60;
    const h = model.height || 60;

    const canvasX = data.x + w / 2 + 8;
    const canvasY = data.y - h / 2 - 4;

    const [htmlX, htmlY] = this._lf.graphModel.transformModel.CanvasPointToHtmlPoint([canvasX, canvasY]);

    this._padEl.style.left = htmlX + 'px';
    this._padEl.style.top = htmlY + 'px';
  }

  _buildButtons(nodeData) {
    this._padEl.innerHTML = '';
    const type = nodeData.type;

    // ── Participant (Pool) ──
    if (type === 'epx:Participant') {
      this._addBtn(ICONS.addLane, 'Agregar Lane', () => this._addLaneToPool(nodeData));
      this._addDivider();
      this._addBtn(ICONS.edit, 'Editar', () => this._editNode(nodeData));
      this._addBtn(ICONS.delete, 'Eliminar', () => this._deleteNode(nodeData), true);
      return;
    }

    // ── Lane ──
    if (type === 'epx:Lane') {
      this._addBtn(ICONS.addLaneAbove, 'Agregar Lane Arriba', () => this._addLaneAbove(nodeData));
      this._addBtn(ICONS.addLaneBelow, 'Agregar Lane Abajo', () => this._addLaneBelow(nodeData));
      this._addDivider();
      this._addBtn(ICONS.edit, 'Editar', () => this._editNode(nodeData));
      this._addBtn(ICONS.removeLane, 'Quitar Lane', () => this._removeLane(nodeData), true);
      return;
    }

    const isEnd = type.includes('EndEvent');
    const isEventBasedGateway = type === 'epx:EventBasedGateway';

    // ── Add node button → opens append panel ──
    if (!isEnd) {
      this._addNodeBtn = this._addBtn(
        ICONS.addNode,
        'Agregar nodo',
        () => this._showAppendPanel(nodeData, isEventBasedGateway),
        false,
        true  // toggleable
      );
    }

    // ── Text Annotation ──
    this._addBtn(ICONS.textAnnotation, 'Anotación', () => this._appendAnnotation(nodeData));

    this._addDivider();

    // ── Change type ──
    this._addBtn(ICONS.replace, 'Cambiar tipo', () => this._showReplaceMenu(nodeData));

    // ── Edit & Delete ──
    this._addBtn(ICONS.edit, 'Editar', () => this._editNode(nodeData));
    this._addBtn(ICONS.delete, 'Eliminar', () => this._deleteNode(nodeData), true);
  }

  _addBtn(iconSvg, tooltip, onClick, isDanger = false, toggleable = false) {
    const btn = document.createElement('button');
    btn.className = 'epx-cp-btn' + (isDanger ? ' epx-cp-danger' : '');
    btn.innerHTML = iconSvg;
    btn.setAttribute('data-tooltip', tooltip);
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      onClick();
      if (toggleable) {
        btn.classList.toggle('epx-cp-active', !!this._appendPanelEl);
      }
    });
    this._padEl.appendChild(btn);
    return btn;
  }

  _addDivider() {
    const divider = document.createElement('div');
    divider.className = 'epx-cp-divider';
    this._padEl.appendChild(divider);
  }

  // ── Append panel ─────────────────────────────────────────────────────────────

  _showAppendPanel(nodeData, isEventBasedGateway) {
    // Toggle: click again → close
    if (this._appendPanelEl) {
      this._hideAppendPanel();
      this._updateAddNodeBtnState(false);
      return;
    }

    const tabs = isEventBasedGateway ? APPEND_TABS_EVENTGW : APPEND_TABS;
    const initialTab = Object.keys(tabs)[0];
    const panel = this._buildAppendPanel(nodeData, tabs, initialTab);

    this._appendPanelEl = panel;

    const container = this._lf.graphModel.rootEl;
    container.appendChild(panel);
    this._positionAppendPanel(nodeData);
    this._updateAddNodeBtnState(true);

    setTimeout(() => {
      this._appendPanelClickHandler = (e) => {
        if (
          this._appendPanelEl &&
          !this._appendPanelEl.contains(e.target) &&
          !this._padEl.contains(e.target)
        ) {
          this._hideAppendPanel();
          this._updateAddNodeBtnState(false);
        }
      };
      document.addEventListener('click', this._appendPanelClickHandler);
    }, 0);
  }

  _hideAppendPanel() {
    if (this._appendPanelEl && this._appendPanelEl.parentNode) {
      this._appendPanelEl.parentNode.removeChild(this._appendPanelEl);
    }
    this._appendPanelEl = null;

    if (this._appendPanelClickHandler) {
      document.removeEventListener('click', this._appendPanelClickHandler);
      this._appendPanelClickHandler = null;
    }
  }

  _updateAddNodeBtnState(active) {
    if (this._addNodeBtn) {
      this._addNodeBtn.classList.toggle('epx-cp-active', active);
    }
  }

  _buildAppendPanel(nodeData, tabs, initialTab) {
    const panel = document.createElement('div');
    panel.className = 'epx-append-panel';

    // Header
    const hdr = document.createElement('div');
    hdr.className = 'epx-ap-header';

    const title = document.createElement('div');
    title.className = 'epx-ap-title';
    title.textContent = 'Agregar Nodo';
    hdr.appendChild(title);

    const closeBtn = document.createElement('button');
    closeBtn.className = 'epx-ap-close';
    closeBtn.innerHTML = '&#x2715;';
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this._hideAppendPanel();
      this._updateAddNodeBtnState(false);
    });
    hdr.appendChild(closeBtn);
    panel.appendChild(hdr);

    // Tabs
    const tabKeys = Object.keys(tabs);
    let activeTab = initialTab;

    const tabsRow = document.createElement('div');
    tabsRow.className = 'epx-ap-tabs';

    const body = document.createElement('div');
    body.className = 'epx-ap-body';

    const renderBody = (tabKey) => {
      body.innerHTML = '';
      const tabDef = tabs[tabKey];
      for (const group of tabDef.groups) {
        const groupLabel = document.createElement('div');
        groupLabel.className = 'epx-ap-group-label';
        groupLabel.textContent = group.label;
        body.appendChild(groupLabel);

        const grid = document.createElement('div');
        grid.className = 'epx-ap-grid';

        for (const item of group.items) {
          const el = document.createElement('div');
          el.className = 'epx-ap-item';

          const iconBox = document.createElement('div');
          iconBox.className = 'epx-ap-icon';
          iconBox.innerHTML = ICONS[item.icon] || '';
          el.appendChild(iconBox);

          const lbl = document.createElement('div');
          lbl.className = 'epx-ap-item-label';
          lbl.textContent = item.shortLabel;
          el.appendChild(lbl);

          el.addEventListener('click', (e) => {
            e.stopPropagation();
            this._appendNode(item.type, nodeData);
            this._hideAppendPanel();
            this._updateAddNodeBtnState(false);
          });

          grid.appendChild(el);
        }
        body.appendChild(grid);
      }
    };

    for (const key of tabKeys) {
      const tab = document.createElement('div');
      tab.className = 'epx-ap-tab' + (key === activeTab ? ' epx-ap-tab-active' : '');
      tab.textContent = tabs[key].label;
      tab.addEventListener('click', (e) => {
        e.stopPropagation();
        if (activeTab === key) return;
        activeTab = key;
        tabsRow.querySelectorAll('.epx-ap-tab').forEach((t, i) => {
          t.classList.toggle('epx-ap-tab-active', tabKeys[i] === key);
        });
        renderBody(key);
      });
      tabsRow.appendChild(tab);
    }

    panel.appendChild(tabsRow);
    renderBody(activeTab);
    panel.appendChild(body);

    return panel;
  }

  _positionAppendPanel(nodeData) {
    if (!this._appendPanelEl) return;

    const model = this._lf.graphModel.getElement(nodeData.id);
    if (!model) return;

    const w = model.width || 60;
    const h = model.height || 60;

    // Position: to the right of the context pad (which is right of node)
    const canvasX = nodeData.x + w / 2 + 48;
    const canvasY = nodeData.y - h / 2 - 4;
    const [htmlX, htmlY] = this._lf.graphModel.transformModel.CanvasPointToHtmlPoint([canvasX, canvasY]);

    const container = this._lf.graphModel.rootEl;
    const containerRect = container.getBoundingClientRect();
    const panelW = 292;
    const panelH = 340;

    let left = htmlX;
    let top = htmlY;

    // Flip left if panel goes off right edge
    if (left + panelW > containerRect.width - 8) {
      const [flippedX] = this._lf.graphModel.transformModel.CanvasPointToHtmlPoint(
        [nodeData.x - w / 2 - panelW - 48, canvasY]
      );
      left = flippedX;
    }
    if (top + panelH > containerRect.height - 8) {
      top = Math.max(8, containerRect.height - panelH - 8);
    }
    if (top < 8) top = 8;

    this._appendPanelEl.style.left = left + 'px';
    this._appendPanelEl.style.top = top + 'px';
  }

  _appendNode(type, sourceData) {
    const lf = this._lf;
    const sourceModel = lf.graphModel.getElement(sourceData.id);
    if (!sourceModel) return;

    let targetX = sourceData.x + NEXT_X;
    let targetY = sourceData.y;

    // Check for overlaps and shift down
    for (let attempts = 0; attempts < 10; attempts++) {
      const area = lf.getAreaElement(
        [targetX - 70, targetY - 40],
        [targetX + 70, targetY + 40]
      );
      if (!area || area.length === 0) break;
      targetY += 80;
    }

    const newNode = lf.addNode({
      type,
      x: targetX,
      y: targetY,
    });

    if (newNode) {
      lf.addEdge({
        sourceNodeId: sourceData.id,
        targetNodeId: newNode.id,
        type: 'epx:SequenceFlow',
      });
    }

    this._hide();
  }

  _appendBoundaryEvent(nodeData) {
    const lf = this._lf;
    const model = lf.graphModel.getElement(nodeData.id);
    if (!model) return;

    const w = model.width || 120;
    const h = model.height || 60;

    // Place boundary event at bottom-right of the activity
    const bx = nodeData.x + w / 2 - 15;
    const by = nodeData.y + h / 2;

    const newNode = lf.addNode({
      type: 'epx:BoundaryEvent',
      x: bx,
      y: by,
      properties: {
        attachedToRef: nodeData.id,
      },
    });

    this._hide();
  }

  _appendAnnotation(nodeData) {
    const lf = this._lf;
    const targetX = nodeData.x;
    const targetY = nodeData.y - 100;

    const newNode = lf.addNode({
      type: 'epx:TextAnnotation',
      x: targetX,
      y: targetY,
    });

    if (newNode) {
      lf.addEdge({
        sourceNodeId: nodeData.id,
        targetNodeId: newNode.id,
        type: 'epx:Association',
      });
    }

    this._hide();
  }

  _showReplaceMenu(nodeData) {
    if (this._replaceMenu) {
      this._replaceMenu.show(nodeData, this._padEl);
    }
    this._hide();
  }

  // ── Lane operations ─────────────────────────────────────────────────────────

  _addLaneToPool(nodeData) {
    if (!this._containment) return;
    this._containment.addLaneToPool(nodeData.id);
    this._hide();
  }

  _addLaneAbove(nodeData) {
    if (!this._containment) return;
    this._containment.addLaneAbove(nodeData.id);
    this._hide();
  }

  _addLaneBelow(nodeData) {
    if (!this._containment) return;
    this._containment.addLaneBelow(nodeData.id);
    this._hide();
  }

  _removeLane(nodeData) {
    if (!this._containment) return;
    this._containment.removeLane(nodeData.id);
    this._hide();
  }

  _editNode(nodeData) {
    const model = this._lf.graphModel.getElement(nodeData.id);
    if (!model) return;
    this._hide();

    // Shapes that render their own SVG text from properties.name must not use
    // LogicFlow's editText (which writes to model.text.value and creates a
    // second visible label). Open an HTML inline editor over the shape and
    // write the committed value to properties.name instead.
    if (model.text && model.text.editable === false) {
      this._editCustomTextNode(model, nodeData);
      return;
    }

    this._lf.graphModel.editText(nodeData.id);
  }

  _editCustomTextNode(model, nodeData) {
    const lf = this._lf;
    const tm = lf.graphModel.transformModel;
    const [htmlX, htmlY] = tm.CanvasPointToHtmlPoint([model.x, model.y]);

    const input = document.createElement('input');
    input.type = 'text';
    input.value = (model.properties && model.properties.name) || '';
    input.className = 'epx-inline-text-edit';
    input.style.cssText =
      'position:absolute;transform:translate(-50%,-50%);' +
      `left:${htmlX}px;top:${htmlY}px;` +
      `min-width:${Math.max(40, (model.width || 100) - 20)}px;` +
      'padding:2px 4px;border:1px solid #1890ff;border-radius:3px;' +
      'font:12px "Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif;' +
      'text-align:center;outline:none;z-index:1000;background:#fff;';

    const container = lf.container;
    container.appendChild(input);
    input.focus();
    input.select();

    let committed = false;
    const commit = () => {
      if (committed) return;
      committed = true;
      const val = input.value.trim();
      if (input.parentNode) input.parentNode.removeChild(input);
      lf.setProperties(nodeData.id, { name: val });
      lf.emit('node:properties-change', { id: nodeData.id, properties: { name: val } });
    };
    const cancel = () => {
      if (committed) return;
      committed = true;
      if (input.parentNode) input.parentNode.removeChild(input);
    };

    input.addEventListener('blur', commit);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); commit(); }
      else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
    });
  }

  _deleteNode(nodeData) {
    this._hide();
    this._lf.deleteNode(nodeData.id);
  }

  destroy() {
    this._hideAppendPanel();
    if (this._padEl && this._padEl.parentNode) {
      this._padEl.parentNode.removeChild(this._padEl);
    }
  }
}
